import { Email, LocalPhone, PersonRounded, Smartphone } from '@mui/icons-material';
import { Box } from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../../AppContext';
import { Actions } from '../../../enums/ActionEnums';
import { Types } from '../../../enums/TypeEnums';
import {
  IContact,
  useAssistantContactsFromSameCompanyLazyQuery,
  useUpdateContactAssistantMutation
} from '../../../generated/graphql';
import { canUserEdit } from '../../../utils/rightUtils';
import { Colors } from '../../../utils/theme';
import CancelButton from '../../Common/Buttons/CancelButton';
import EditButton from '../../Common/Buttons/EditButton';
import SaveButton from '../../Common/Buttons/SaveButton';
import AutocompleteComponent from '../../Common/Fields/AutocompleteComponent';
import InputComponent from '../../Common/Fields/InputComponent';

type IProps = {
  contact: IContact;
};

const ContactAssistantTab: React.FunctionComponent<IProps> = ({ contact }: IProps) => {
  const [editable, setEditable] = useState(false);
  const [assistantOptions, setAssistantOptions] = useState([]);
  const [assistant, setAssistant] = useState(contact.assistant);
  const { dispatch, state } = useContext(AppContext);
  const canEdit = canUserEdit(state.user, Types.Contact);

  const [getPossibleAssistants, getPossibleAssistantsResult] =
    useAssistantContactsFromSameCompanyLazyQuery();
  const [updateAssistant, updateAssistantResult] = useUpdateContactAssistantMutation();

  useEffect(() => {
    if (getPossibleAssistantsResult.data && getPossibleAssistantsResult.data.result) {
      setAssistantOptions(getPossibleAssistantsResult.data.result as never[]);
    }
  }, [getPossibleAssistantsResult]);

  useEffect(() => {
    if (updateAssistantResult.data && updateAssistantResult.data.result) {
      setEditable(false);
      dispatch({
        type: Actions.ShowMessage,
        payload: {
          text: `Successfully updated ${updateAssistantResult.data.result.fullName} assistant`,
          severity: 'success'
        }
      });
    }
  }, [updateAssistantResult.data]);

  const handleCancel = () => {
    setEditable(false);
    setAssistant(contact.assistant);
  };

  const handleSave = () => {
    if (contact.assistant?.id !== assistant?.id) {
      updateAssistant({
        variables: {
          id: parseInt(contact.id),
          assistantId: assistant !== null ? parseInt(assistant?.id as string) : null
        }
      }).catch((e) => {
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            severity: 'error',
            text: e.message,
            autoHide: 6000
          }
        });
      });
    } else {
      setEditable(false);
    }
  };

  const handleAssistantChanged = (_: unknown, newValue: IContact | null) => {
    setAssistant(newValue);
  };

  const handleEditClicked = () => {
    getPossibleAssistants({
      variables: { id: parseInt(contact.id), company_id: parseInt(contact.company.id) }
    });
    setEditable(true);
  };

  return (
    <Box sx={{ padding: '80px 25%', position: 'relative' }}>
      <Box sx={{ position: 'absolute', top: '24px', right: '24px' }}>
        <EditButton
          id="contact-assistant-edit-button"
          onClick={handleEditClicked}
          disabled={!canEdit || editable}
          editButtonColor={!canEdit || editable ? Colors.gray : Colors.skyBlue}
        />
      </Box>
      <Box sx={{ display: 'flex', flexFlow: 'column', gap: '50px' }}>
        <AutocompleteComponent
          id="contact-assistant-name"
          label="Assistant"
          options={assistantOptions}
          getOptionLabel={(option: IContact) => option?.fullName}
          disabled={!editable || updateAssistantResult.loading}
          values={assistant}
          onChange={handleAssistantChanged}
          startAdornment={
            <PersonRounded
              sx={{
                marginRight: '12px',
                color: !editable || updateAssistantResult.loading ? Colors.gray : Colors.main
              }}
            />
          }
        />
        <InputComponent
          id="contact-assistant-email"
          label="Email"
          value={assistant?.email || ''}
          disabled={true}
          startAdornment={
            <Email
              sx={{
                marginRight: '12px',
                color: Colors.gray
              }}
            />
          }
        />
        <InputComponent
          id="contact-assistant-phone"
          label="Phone Number"
          value={assistant?.phone_direct || ''}
          disabled={true}
          startAdornment={
            <LocalPhone
              sx={{
                marginRight: '12px',
                color: Colors.gray
              }}
            />
          }
        />
        <InputComponent
          id="contact-assistant-mobile"
          label="Mobile"
          value={assistant?.phone_mobile || ''}
          disabled={true}
          startAdornment={
            <Smartphone
              sx={{
                marginRight: '12px',
                color: Colors.gray
              }}
            />
          }
        />
      </Box>
      {editable && (
        <Box className="centralize" sx={{ marginTop: '160px' }}>
          <CancelButton
            id="contact-assistant-cancel-button"
            disabled={!editable || updateAssistantResult.loading}
            onClick={handleCancel}
            text="Cancel"
          />
          <SaveButton
            id="contact-assistant-save-button"
            disabled={!editable || updateAssistantResult.loading}
            onClick={handleSave}
            text="Save"
          />
        </Box>
      )}
    </Box>
  );
};

export default ContactAssistantTab;
